import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { isSuperAdmin } from "../utils/permissions";
import UserMenu from "../components/UserMenu";

const CONFIG_META: Record<string, { label: string; description: string; type: string }> = {
  app_name: { label: "Application Name", description: "Display name of the application", type: "text" },
  app_logo_url: { label: "Logo URL", description: "URL to the application logo image", type: "text" },
  cpi_warning_threshold: { label: "CPI Warning Threshold", description: "CPI below this value shows amber warning", type: "number" },
  cpi_critical_threshold: { label: "CPI Critical Threshold", description: "CPI below this value shows red critical", type: "number" },
  access_request_auto_notify: { label: "Auto Notify on Access Request", description: "Notify admins automatically when access is requested", type: "boolean" },
  report_frequency_default: { label: "Default Report Frequency", description: "Default reporting cadence for new projects", type: "text" },
  max_file_upload_mb: { label: "Max File Upload Size (MB)", description: "Maximum allowed file upload size in megabytes", type: "number" },
};

export default function Settings() {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSuperAdmin()) { navigate("/select-project"); return; }
    api.get("/api/config").then(r => { setConfig(r.data); setEdited(r.data); }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/api/config", edited);
      setConfig({ ...edited });
      setMsg("Configuration saved successfully.");
    } catch {
      setMsg("Failed to save configuration.");
    } finally { setSaving(false); }
  };

  const handleAddKey = async () => {
    if (!newKey.trim() || !newValue.trim()) return;
    const updated = { ...edited, [newKey.trim()]: newValue.trim() };
    setEdited(updated);
    setNewKey(""); setNewValue("");
  };

  const handleDeleteKey = async (key: string) => {
    if (!window.confirm(`Delete config key "${key}"?`)) return;
    try {
      await api.delete(`/api/config/${key}`);
      const updated = { ...edited };
      delete updated[key];
      setEdited(updated);
      setConfig(updated);
    } catch {}
  };

  const knownKeys = Object.keys(CONFIG_META).filter(k => k in edited);
  const customKeys = Object.keys(edited).filter(k => !(k in CONFIG_META));

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/select-project")}>
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">C</span>
          </div>
          <div>
            <div className="text-xs text-gray-400 leading-none">COLLINS</div>
            <div className="text-sm font-semibold text-gray-900 leading-tight">EAC Reporting</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => navigate("/select-project")} className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Dashboard</button>
          <button onClick={() => navigate("/users")} className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">User Management</button>
          <button className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium">Settings</button>
        </div>
        <UserMenu />
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">SETTINGS</h1>
          <p className="text-xs text-gray-400 mt-0.5">Application configuration — super admin only.</p>
        </div>

        {msg && (
          <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${msg.includes("success") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
            {msg}
          </div>
        )}

        {/* Known config keys */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Application Settings</h2>
          <div className="space-y-4">
            {knownKeys.map(key => {
              const meta = CONFIG_META[key];
              return (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">{meta.label}</label>
                  <p className="text-xs text-gray-400 mb-1">{meta.description}</p>
                  {meta.type === "boolean" ? (
                    <select value={edited[key]} onChange={e => setEdited({ ...edited, [key]: e.target.value })}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  ) : (
                    <input type={meta.type} value={edited[key] || ""} onChange={e => setEdited({ ...edited, [key]: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom keys */}
        {customKeys.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Custom Configuration</h2>
            <div className="space-y-3">
              {customKeys.map(key => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-500 w-40 truncate">{key}</span>
                  <input value={edited[key]} onChange={e => setEdited({ ...edited, [key]: e.target.value })}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button onClick={() => handleDeleteKey(key)} className="text-red-500 hover:text-red-700 text-xs px-2">Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add custom key */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Add Custom Key</h2>
          <div className="flex gap-3">
            <input placeholder="Key name" value={newKey} onChange={e => setNewKey(e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input placeholder="Value" value={newValue} onChange={e => setNewValue(e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button onClick={handleAddKey} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900">Add</button>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
}
